import type { AccessScope, AuthenticatedSession } from './auth/types';

export function isHeadquartersScope(scope: AccessScope) {
  return (
    ['hqSafety', 'hqAudit'].includes(scope.role) &&
    ['hq-safety-admin', 'hq-staff', 'hq-auditor'].includes(scope.persona)
  );
}

/** Navigation only: every choice is an unchanged scope issued by the identity server. */
export function headquartersSiteChoices(session: AuthenticatedSession) {
  const active = session.scopes.find((scope) => scope.id === session.activeScopeId);
  if (!active || !isHeadquartersScope(active)) return [];
  const sameAuthority = session.scopes.filter(
    (scope) =>
      isHeadquartersScope(scope) &&
      scope.persona === active.persona &&
      scope.role === active.role &&
      scope.resource.tenantId === active.resource.tenantId,
  );
  const headquarters = sameAuthority.find((scope) => scope.resource.kind === 'tenant');
  if (!headquarters) return [];
  const factories = sameAuthority
    .filter(
      (scope) =>
        scope.resource.kind === 'site' &&
        scope.capabilities.includes('document:read') &&
        scope.capabilities.includes('records:enterprise-read'),
    )
    .sort((left, right) => left.label.localeCompare(right.label, 'ko-KR'));
  return factories.length ? [headquarters, ...factories] : [];
}

/** Move off the old feature before the identity observer mounts the new scope. */
export async function switchWorkspaceScope(
  session: AuthenticatedSession,
  scopeId: string,
  actions: {
    resetLocation: () => () => void;
    clearCaches: () => void;
    selectScope: (id: string) => Promise<void>;
  },
) {
  if (scopeId === session.activeScopeId) return;
  if (!session.scopes.some((scope) => scope.id === scopeId))
    throw new Error('WORKSPACE_SCOPE_UNAVAILABLE');
  const restoreLocation = actions.resetLocation();
  actions.clearCaches();
  try {
    await actions.selectScope(scopeId);
  } catch (error) {
    restoreLocation();
    throw error;
  }
}
